import React, { useEffect, useMemo, useState } from "react"

import {
  useDeleteTestSetAutoAddRuleTransport,
  useSaveTestSetAutoAddRuleTransport,
  useTestSetAutoAddRuleTransport,
} from "@/test-set/transport"
import {
  Button,
  FormField,
  Input,
  Skeleton,
  Switch,
  Textarea,
  toast,
} from "@coval/ui"

import {
  AUTO_ADD_BUDGET_RANGE,
  AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH,
  AUTO_ADD_PENDING_CAP_RANGE,
  autoAddRuleFieldsFromForm,
  autoAddRuleFormFromFields,
  DEFAULT_AUTO_ADD_RULE_FORM,
  validateAutoAddRuleForm,
  type AutoAddRuleFormState,
} from "../../utils/autoAddRuleForm"
import {
  AgentFilterSelect,
  MetadataFilterRows,
  MetricValueFilterRows,
} from "./AutoAddRuleFilterRows"

interface TestSetAutoAddRulePanelProps {
  testSetId: string
}

/**
 * Settings for auto-adding uploaded conversations to this test set. Saves the
 * whole rule on each Save; there is at most one rule per test set.
 */
export const TestSetAutoAddRulePanel: React.FC<
  TestSetAutoAddRulePanelProps
> = ({ testSetId }) => {
  const ruleQuery = useTestSetAutoAddRuleTransport(testSetId)
  const saveRule = useSaveTestSetAutoAddRuleTransport()
  const deleteRule = useDeleteTestSetAutoAddRuleTransport()
  const [form, setForm] = useState<AutoAddRuleFormState>(
    DEFAULT_AUTO_ADD_RULE_FORM,
  )
  const [isDirty, setIsDirty] = useState(false)

  const storedRule = ruleQuery.data ?? null
  // Re-seed the form from the stored rule until the user edits it.
  useEffect(() => {
    if (!isDirty) {
      setForm(autoAddRuleFormFromFields(storedRule))
    }
  }, [isDirty, storedRule])

  const errors = useMemo(() => validateAutoAddRuleForm(form), [form])
  const hasErrors = Object.keys(errors).length > 0

  const patch = (changes: Partial<AutoAddRuleFormState>) => {
    setIsDirty(true)
    setForm((previous) => ({ ...previous, ...changes }))
  }

  const handleSave = async () => {
    if (hasErrors) {
      return
    }
    try {
      await saveRule.mutateAsync({
        test_set_id: testSetId,
        rule: autoAddRuleFieldsFromForm(form),
      })
      setIsDirty(false)
      toast.success({ title: "Auto-add rule saved" })
    } catch (error: unknown) {
      toast.danger({
        title: "Could not save the auto-add rule",
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  const handleRemove = async () => {
    try {
      await deleteRule.mutateAsync({ test_set_id: testSetId })
      setIsDirty(false)
      setForm(DEFAULT_AUTO_ADD_RULE_FORM)
      toast.success({ title: "Auto-add rule removed" })
    } catch (error: unknown) {
      toast.danger({
        title: "Could not remove the auto-add rule",
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  if (ruleQuery.error) {
    return (
      <div className="flex items-center gap-3 text-sm text-coval-danger-text">
        <span>Could not load the auto-add rule.</span>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void ruleQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }
  if (ruleQuery.isLoading) {
    return (
      <Skeleton
        className="h-24 w-full rounded-md"
        data-testid="auto-add-rule-loading"
      />
    )
  }

  const isSaving = saveRule.isPending || deleteRule.isPending

  return (
    <form
      className="flex flex-col gap-4"
      data-testid="auto-add-rule-panel"
      onSubmit={(event) => {
        event.preventDefault()
        void handleSave()
      }}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-coval-text">
            Auto-add from monitoring
          </p>
          <p className="text-xs text-coval-text-muted">
            Uploaded conversations that match these filters are checked against
            this test set. Uncovered scenarios are added for review.
          </p>
        </div>
        <Switch
          aria-label="Enable auto-add"
          checked={form.enabled}
          onCheckedChange={(checked) => patch({ enabled: checked })}
        />
      </div>

      <FormField label="Metadata filters" error={errors.metadataFilters}>
        <MetadataFilterRows
          filters={form.metadataFilters}
          onChange={(metadataFilters) => patch({ metadataFilters })}
        />
      </FormField>

      <FormField label="Metric filters" error={errors.metricValueFilters}>
        <MetricValueFilterRows
          filters={form.metricValueFilters}
          onChange={(metricValueFilters) => patch({ metricValueFilters })}
        />
      </FormField>

      <FormField
        label="Agents"
        description="Leave empty to match any agent."
        error={errors.agentIds}
      >
        <AgentFilterSelect
          agentIds={form.agentIds}
          onChange={(agentIds) => patch({ agentIds })}
        />
      </FormField>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <FormField
          className="flex flex-col [&_input]:mt-auto"
          label="Sample rate (%)"
          description="Share of matching conversations that get a coverage check."
          error={errors.sampleRatePercent}
        >
          <Input
            aria-label="Sample rate percent"
            inputMode="decimal"
            value={form.sampleRatePercent}
            onChange={(event) =>
              patch({ sampleRatePercent: event.target.value })
            }
          />
        </FormField>
        <FormField
          className="flex flex-col [&_input]:mt-auto"
          label="Checks per day"
          description={`${AUTO_ADD_BUDGET_RANGE.min} to ${AUTO_ADD_BUDGET_RANGE.max}.`}
          error={errors.evaluationBudgetPerDay}
        >
          <Input
            aria-label="Evaluation budget per day"
            inputMode="numeric"
            value={form.evaluationBudgetPerDay}
            onChange={(event) =>
              patch({ evaluationBudgetPerDay: event.target.value })
            }
          />
        </FormField>
        <FormField
          className="flex flex-col [&_input]:mt-auto"
          label="Max pending for review"
          description={`${AUTO_ADD_PENDING_CAP_RANGE.min} to ${AUTO_ADD_PENDING_CAP_RANGE.max}. Adding pauses at this many unreviewed test cases.`}
          error={errors.maxPendingTestCases}
        >
          <Input
            aria-label="Max pending test cases"
            inputMode="numeric"
            value={form.maxPendingTestCases}
            onChange={(event) =>
              patch({ maxPendingTestCases: event.target.value })
            }
          />
        </FormField>
      </div>

      <FormField
        label="Prompt guidance"
        optional
        description={`Extra instructions for the coverage check, up to ${AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH} characters.`}
        error={errors.promptGuidance}
      >
        <Textarea
          aria-label="Prompt guidance"
          rows={3}
          maxLength={AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH}
          placeholder="Only add scenarios that differ materially from existing test cases."
          value={form.promptGuidance}
          onChange={(event) => patch({ promptGuidance: event.target.value })}
        />
      </FormField>

      <div className="flex items-center justify-end gap-2">
        {storedRule && (
          <Button
            type="button"
            variant="danger"
            disabled={isSaving}
            onClick={() => void handleRemove()}
          >
            Remove rule
          </Button>
        )}
        <Button
          type="submit"
          variant="default"
          disabled={isSaving || hasErrors || (!isDirty && Boolean(storedRule))}
          loading={saveRule.isPending}
        >
          {storedRule ? "Save rule" : "Enable auto-add"}
        </Button>
      </div>
    </form>
  )
}
